import { useState, useEffect } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useMediaQuery } from "@mui/material";
import Image from "@Components/Image";
import {
  getSearchHits,
  getSuggestionsHits,
} from "@Components/search/utils/searchUtils";

import { Highlighted } from "@Components/ui/Highlighted/Highlighted";
import { ADVANCE_SEARCH_MIN_PRODUCT_COUNT } from "@Constants/common";
import { hitsResHit } from "@Types/SearchUtils";
import { isCanadianLocale } from "@Utils/locale";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import SearchInputPredictProductCA from "./SearchInputPredictProductCA";
import classes from "./SearchInputPredictCA.module.scss";

interface hitsRes {
  slice: Function;
  hits?: hitsResHit;
}

interface ISearchInputPredictProps {
  term: string;
  focused: boolean;
  onSuggestionClick: Function;
}

const CALL_MADE_ARROW_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAABmJLR0QA/wD/AP+gvaeTAAAA/0lEQVRIie3UPQrCMBgG4PcL5ggK3sBBKbQdnKsXsDd1E0HBySUWig7eQNADOARblzrYlv6kX0TEF7Lkh4eE8AL//HooP6GU2gOY1py7E1Hoed7KFBYlc3UoS3o162cAge/7F2647MavnACMAKzjOB58DBZCBACOAMZa651SavgR2HXdmxBiluEjAFtOvOqpreKVsE28FraFl8H3bBRwKeUcTL+9ABNRSESLss2O41yllLMMH2utN6Z4oTKbJIqifpIkGwATGJaMEcyBG8Nd8U5wF7wzbIqzwCY4G9wWZ4Xb4OxwU7xRV7dNvl6JaJnfYwUG3ur1kKbpw5bzz/fmCSnOmW1flsQGAAAAAElFTkSuQmCC";

export default function SearchInputPredictForCA(
  props: ISearchInputPredictProps
) {
  const currentLocale = useAppSelector(localeSelector);
  const isCanadianUser = isCanadianLocale(currentLocale);
  const isMobileOrTablet = useMediaQuery("(max-width: 1224px)");

  const [activeItem, setActiveItem] = useState("");
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [suggestionList, setSuggestionList] = useState([]);
  const [productSuggestionList, setProductSuggestionList] = useState([]);
  const maxSuggestions = 10;
  const { term, focused, onSuggestionClick } = props;
  const validAndFocused = term.length > 2 && focused;

  /**
   * The function "updateSuggestionTags" sets the suggestion list by taking a subset of hits.
   * @param {hitsRes} hits - The `hits` parameter is of type `hitsRes`.
   */
  const updateSuggestionTags = (hits: hitsRes) => {
    setSuggestionList(hits.slice(0, maxSuggestions));
  };

  /**
   * The function updates the suggested products list by setting it to a subset of the hits array.
   * @param {hitsRes} hits - The `hits` parameter is an array of objects representing the search
   * results. Each object in the array represents a product.
   */
  const updateSuggestedProducts = (hits: hitsRes) => {
    setProductSuggestionList(hits.slice(0, ADVANCE_SEARCH_MIN_PRODUCT_COUNT));
  };

  /**
   * The function `handleMouseOver` sets the active item and active item index based on the provided
   * query and index number, but only if the query is not empty and the device is not a mobile device.
   * @param {string} query - The `query` parameter is a string that represents the current query or
   * search term.
   * @param {number} indexNumber - The `indexNumber` parameter is a number that represents the index of
   * the item being hovered over.
   */
  const handleMouseOver = (query: string, indexNumber: number) => {
    if (!isMobileOrTablet && query.length > 0) {
      setActiveItem(query);
      setActiveItemIndex(indexNumber);
    }
  };

  useEffect(() => {
    if (!term || term.length < 3) {
      setSuggestionList([]);
      return;
    }

    getSuggestionsHits(term, updateSuggestionTags, isCanadianUser);
  }, [term, isCanadianUser]);

  useEffect(() => {
    getSearchHits(updateSuggestedProducts, activeItem);
    setActiveItem(suggestionList[activeItemIndex]?.["query"]);
  }, [activeItem, activeItemIndex, suggestionList]);

  return (
    <div
      className={classes["resultsCA"]}
      style={{
        display: validAndFocused && suggestionList.length ? "flex" : "none",
        border: isMobileOrTablet ? "1px solid gray" : "none ",
      }}
      data-testid="container-autocomplete-results"
    >
      <div
        className={classes[isMobileOrTablet ? "" : "suggestionWrapper"]}
        style={{
          flex: isMobileOrTablet ? "auto" : "0 0 33.3333333333%",
        }}
      >
        {validAndFocused ? (
          suggestionList.map((result: any, index: number) => (
            <div
              className={`${classes["item-wrapperCA"]} ${
                classes[activeItemIndex === index ? "active" : "inactive"]
              }`}
              key={result?.query}
              onMouseOver={() => handleMouseOver(result?.query, index)}
              onKeyDown={() => handleMouseOver(result?.query, index)}
              onClick={() => onSuggestionClick(result?.query)}
              data-testid="autocomplete-results"
            >
              <div className={classes["item-resultCA"]}>
                <Highlighted text={result?.query} highlight={term} />

                {!isMobileOrTablet ? (
                  <ChevronRightIcon />
                ) : (
                  <Image
                    src={CALL_MADE_ARROW_IMAGE}
                    width={18}
                    height={18}
                    alt="store"
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <></>
        )}
      </div>
      {!isMobileOrTablet && (
        <SearchInputPredictProductCA
          productList={productSuggestionList}
          activeItem={activeItem}
        />
      )}
    </div>
  );
}
